import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { useField } from 'informed';
import { components, DropdownIndicatorProps } from 'react-select';
import { Icon } from 'components/common/icons';
import { SelectInput } from 'components/common/form/common';

const getTimes = (interval: number) => {
  if (!interval) return [];

  let step = interval;
  const options = ['00:00'];
  while (step < 24 * 60) {
    const hour = Math.floor(step / 60);
    const minutes = step % 60;

    options.push(
      `${hour < 10 ? `0${hour}` : hour}:${
        minutes < 10 ? `0${minutes}` : minutes
      }`
    );
    step += interval;
  }

  return options;
};

const DropdownIndicator = (props: DropdownIndicatorProps) => (
  <components.DropdownIndicator {...props}>
    <Icon
      icon={['far', 'clock']}
      style={{ fontSize: '1.4rem', position: 'relative', top: 1, right: 3 }}
    />
  </components.DropdownIndicator>
);

interface Props {
  disabled?: boolean;
  initialValue?: string;
  field: string;
  id: string;
  onChange?: (value: any) => void;
  style?: CSSProperties;
}

const TimePicker = (props: Props) => {
  const { messages } = useReduxState();

  const timePicker = useRef<HTMLDivElement>(undefined);
  const timeFilterOptions = useRef(getTimes(60));

  const [timeInputValue, setTimeInputValue] = useState(
    props.initialValue || '09:00'
  );

  const { fieldState, fieldApi, render, ref, userProps } = useField<string>({
    ...props,
    ...{ timeInputValue }
  });

  const { disabled = false, id, onChange, style } = userProps;

  useEffect(() => {
    // needed to update initial value from field state
    if (fieldState.value !== timeInputValue) {
      setTimeInputValue(fieldState.value);
    }
  }, []);

  const timeStringIsValid = (timeString: string) => {
    return Boolean(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString));
  };

  const handleTimeBlur = (ev) => {
    ev.preventDefault();

    let newValue = timeInputValue;
    if (!timeStringIsValid(timeInputValue)) newValue = fieldState.value;
    if (newValue.length < 5) newValue = `0${newValue}`;

    setTimeInputValue(newValue);
    updateTimeIfValid(newValue);
  };

  const handleTimeChange = (option) => {
    setTimeInputValue(option.value);
    setFieldValue(option.value);
  };

  const handleTimeFocus = () => {
    if (ref.current.inputRef?.setSelectionRange) {
      setTimeout(() => ref.current.inputRef?.setSelectionRange(0, 2));
    }
  };

  const handleTimeInputChange = (inputValue, { action }) => {
    if (action === 'input-blur' || action === 'menu-close') return;

    setTimeInputValue(inputValue);

    updateTimeIfValid(inputValue);

    const caretPosition = ref.current.inputRef?.selectionStart;
    const caretPositionEnd = ref.current.inputRef?.selectionEnd;
    if (caretPosition === 2 && caretPositionEnd === 2) {
      ref.current.inputRef?.setSelectionRange(3, 5);
    }

    fieldApi.setTouched(true);
  };

  const handleTimeKeyDown = (ev: React.KeyboardEvent) => {
    if (
      ev.key === 'Enter' &&
      (!ref.current?.state.prevProps.menuIsOpen ||
        !ref.current?.state.focusedOption)
    ) {
      ev.preventDefault();
      ref.current?.blur();
    } else return;
  };

  const updateTimeIfValid = (value: string) => {
    if (timeStringIsValid(value)) {
      let timeValue = value;

      if (timeValue.length < 5) timeValue = `0${timeValue}`;
      setFieldValue(timeValue);

      // set focused option to selected option
      const matchingOption = ref.current
        .getFocusableOptions()
        .find((option) => option.value === timeValue);
      setTimeout(() => {
        ref.current.setState({ focusedOption: matchingOption || null });
      });
    }
  };

  const setFieldValue = (time: string) => {
    fieldApi.setValue(time);
    fieldApi.validate();
    if (onChange) onChange(time);
  };

  const timeOptions = getTimes(1).map((time) => ({
    value: time,
    label: time
  }));

  return render(
    <div className="time-picker" ref={timePicker} style={style}>
      <SelectInput
        autoComplete="nope"
        controlShouldRenderValue={false}
        components={{ DropdownIndicator }}
        error={fieldState.error}
        filterOption={(option: (typeof timeOptions)[0]) =>
          timeFilterOptions.current.includes(option.value)
        }
        inputIsHidden={false}
        inputValue={timeInputValue}
        instanceId={`time-picker-${id}`}
        isDisabled={disabled}
        isOptionSelected={() => false}
        isSearchable={true}
        noOptionsMessage={() => `${messages.commonCreate}...`}
        onBlur={handleTimeBlur}
        onChange={handleTimeChange}
        onFocus={handleTimeFocus}
        onInputChange={handleTimeInputChange}
        onKeyDown={handleTimeKeyDown}
        options={timeOptions}
        selectRef={ref}
        value={timeOptions.find((option) => option.value === fieldState.value)}
      />

      <style jsx>{`
        .time-picker {
          width: 85px;
        }
        .time-picker :global(.select .react-select__input) {
          visibility: visible;
        }
        .time-picker :global(.select .react-select__single-value) {
          top: calc(50% - 1px);
        }
      `}</style>
    </div>
  );
};

export default TimePicker;
